// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import checkCriditApplicationForm from "@gcg/App/Handlers/CreditApplication/checkCreditApplication";
import { linkStyle02WithAnchor } from "@gcg/App/SharedStyles/LinksStyles";
import { CustomAppState, xCustomCartActions } from "@gcg/App/Store/Reducers";
import { doScroll } from "@gcg/App/Utilities/helpers";
import Button, { ButtonPresentationProps } from "@gcg/MobiusOverrides/Button";
import DatePicker, { DatePickerState } from "@gcg/MobiusOverrides/DatePicker";
import TextArea, { TextAreaProps } from "@gcg/MobiusOverrides/TextArea/TextArea";
import { color, xScreen } from "@gcg/XDL/Utilities";
import { validateEmail } from "@gcg/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, Hidden, Radio, RadioGroup, Typography } from "@insite/mobius/index";
import { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField from "@insite/mobius/TextField";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface CreditApplicationFormProps extends WidgetProps {}

const mapDispatchToProps = {
    checkCriditApplicationForm,
};

const mapStateToProps = (state: CustomAppState & ApplicationState) => {};

type Props = CreditApplicationFormProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CreditApplicationFormWidgetStyles {
    creditApplicationFormWrapper?: InjectableCss;
    accountPayPalContainer?: GridContainerProps;
    purchasingInformationContainer?: GridContainerProps;
    formControlWrapperGridItem?: GridItemProps;
    radioGridItem?: GridItemProps;
    taxRadioGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    agreementTitleGridItem?: GridItemProps;
    formTitle?: TypographyProps;
    addresslWrapperGridItem?: GridItemProps;
    addressGridItem?: GridItemProps;
    yearEstablishedGridItem?: GridItemProps;
    yearEstablishedTextField?: TextFieldProps;
    cityGridItem?: GridItemProps;
    zoneGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
    zipGridItem?: GridItemProps;
    formControlWrapperFullGridItem?: GridItemProps;
    formFullGridItem?: GridItemProps;
    TaxTextGridItem?: GridItemProps;
    InvoiceEmailGridItem?: GridItemProps;
    spacer?: InjectableCss;
    emailAlreadyExists?: InjectableCss;
    title: TypographyProps;
    dollerIcon: TypographyProps;
    titleWrapper: GridItemProps;
    monthlyPurchasessectionTitle?: TypographyProps;
    monthlyPurchasessectionTitleGridItem?: GridItemProps;
    monthlyPurchasesInputGridItem?: GridItemProps;
    placeholderText?: TextFieldPresentationProps;
    textAreaPlaceholder?: TextAreaProps;
    sectionTitle?: TypographyProps;
    formSmallTitle?: TypographyProps;
    organizationRadioGroup?: RadioGroupProps;
    willYourPurchasesBeTaxableRadioGroup?: RadioGroupProps;
    expirationYearGridItem?: GridItemProps;
    clearButton?: ButtonPresentationProps;
    numberOfEmployeesGridItem?: GridItemProps;
    numberOfEmployeesTextField?: TextFieldProps;
    dunsNumberTextField?: TextFieldProps;
}

export const CreditApplicationFormWidgetStyles: CreditApplicationFormWidgetStyles = {
    creditApplicationFormWrapper: {
        css: css`
            position: relative;
        `,
    },
    clearButton: {
        variant: "secondary",
        css: css`
            margin-left: 20px;
            background-color: ${color("common.ui3")};
        `,
    },
    accountPayPalContainer: {
        css: css`
            width: 45%;
            margin-right: 15px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    zoneGridItem: {
        width: 12,
    },
    dollerIcon: {
        css: css`
            margin-right: 10px !important;
        `,
    },
    purchasingInformationContainer: {
        css: css`
            width: 45%;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    TaxTextGridItem: {
        width: 12,
        css: css`
            margin-left: 5px !important;
        `,
    },

    formControlWrapperGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    monthlyPurchasessectionTitleGridItem: {
        width: [12, 6, 6, 6, 9],
        css: css`
            margin-top: 20px !important;
        `,
    },
    InvoiceEmailGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            margin-top: 40px !important;
        `,
    },
    radioGridItem: {
        width: 12,
    },
    taxRadioGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    formFullGridItem: {
        width: 12,
        css: css`
            display: block;
            width: 100%;
        `,
    },
    organizationRadioGroup: {
        css: css`
            display: inline-block;
            padding-bottom: 20px;
            width: 55%;
            flex-direction: row;
            & > div {
                margin-right: 20px;
                display: inline-flex;
            }
        `,
    },
    willYourPurchasesBeTaxableRadioGroup: {
        css: css`
            display: inline-block;
            padding-bottom: 20px;
            width: 35%;
            flex-direction: row;
            & > div {
                margin-right: 65px;
                display: inline-flex;
            }
        `,
    },
    monthlyPurchasessectionTitle: {
        size: 20,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding-left: 20px;
            font-size: 20px;
        `,
    },
    agreementTitleGridItem: {
        width: 12,
        css: css`
            display: inline-block;
            margin: 0 auto !important;
            text-align: center;
        `,
    },
    addressGridItem: {
        width: [12, 6, 6, 4, 4],
    },
    yearEstablishedGridItem: {
        width: [12, 6, 6, 3, 3],
    },
    cityGridItem: {
        width: [12, 6, 6, 3, 3],
    },
    stateGridItem: {
        width: [12, 6, 6, 3, 3],
    },
    zipGridItem: {
        width: [12, 6, 6, 3, 2],
    },
    monthlyPurchasesInputGridItem: {
        width: [12, 6, 6, 3, 3],
        css: css`
            margin-top: 20px !important;
            align-items: baseline;
        `,
    },
    sectionTitle: {
        variant: "h4",
        as: "h2",
        css: css`
            margin: 35px 0;
            font-size: 24px;
        `,
    },
    formTitle: {
        variant: "h2",
    },
    addresslWrapperGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    spacer: {
        css: css`
            height: 55px;
        `,
    },
    formControlWrapperFullGridItem: {
        width: 12,
    },
    emailAlreadyExists: {
        css: css`
            margin-bottom: 15px;
            max-width: 100%;

            ${linkStyle02WithAnchor};
        `,
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    formSmallTitle: {
        css: css`
            font size: 16px;
        `,
    },
    titleWrapper: {
        width: [12, 6, 6, 9, 9],
    },
    placeholderText: {
        cssOverrides: {
            formField: css`
                input::placeholder {
                    font-style: italic;
                }
            `,
        },
    },
    textAreaPlaceholder: {
        cssOverrides: {
            formField: css`
                textarea::placeholder {
                    font-style: italic;
                }
            `,
        },
    },
    numberOfEmployeesGridItem: {
        width: [12, 6, 6, 3, 3],
    },
    yearEstablishedTextField: {
        cssOverrides: {
            formInputWrapper: css`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
            `,
        }
    },
    numberOfEmployeesTextField: {
        cssOverrides: {
            formInputWrapper: css`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
            `,
        }
    },
    dunsNumberTextField: {
        cssOverrides: {
            formInputWrapper: css`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
            `,
        }
    },
};
const Separator = styled.hr`
    width: 100%;
    margin: 20px 0;
`;

const styles = CreditApplicationFormWidgetStyles;

// eslint-disable-next-line no-empty-pattern
const CreditApplicationForm: FC<Props> = ({ checkCriditApplicationForm, id }) => {
    let error: any = false;
    const [legalCustomerName, setLegalCustomerName] = useState("");
    const [salesRepresentative, setSalesRepresentative] = useState("");
    const [billingAddress, setBillingAddress] = useState("");
    const [organizationType, setOrganizationType] = useState("");
    const [bCity, setBCity] = useState("");
    const [indicateAnticipatedMonthlyPurchases, setIndicateAnticipatedMonthlyPurchases] = useState("");
    const [bankName, setBankName] = useState("");
    const [bankContactName, setBankContactName] = useState("");
    const [bankAddress, setBankAddress] = useState("");
    const [bankCity, setBankCity] = useState("");
    const [bankState, setBankState] = useState("");
    const [bankZip, setBankZip] = useState("");
    const [bankPhone, setBankPhone] = useState("");
    const [bankFax, setBankFax] = useState("");
    const [invoicesEmail, setInvoicesEmail] = useState("");
    const [ref1Name, setRef1Name] = useState("");
    const [ref1ContactName, setRef1ContactName] = useState("");
    const [ref1Address, setRef1Address] = useState("");
    const [ref1City, setRef1City] = useState("");
    const [ref1State, setRef1State] = useState("");
    const [ref1Zip, setRef1Zip] = useState("");
    const [ref1Phone, setRef1Phone] = useState("");
    const [ref1Fax, setRef1Fax] = useState("");
    const [ref1Email, setRef1Email] = useState("");
    const [ref2Name, setRef2Name] = useState("");
    const [ref2ContactName, setRef2ContactName] = useState("");
    const [ref2Address, setRef2Address] = useState("");
    const [ref2City, setRef2City] = useState("");
    const [ref2State, setRef2State] = useState("");
    const [ref2Zip, setRef2Zip] = useState("");
    const [ref2Phone, setRef2Phone] = useState("");
    const [ref2Fax, setRef2Fax] = useState("");
    const [ref2Email, setRef2Email] = useState("");
    const [ref3Name, setRef3Name] = useState("");
    const [ref3ContactName, setRef3ContactName] = useState("");
    const [ref3Address, setRef3Address] = useState("");
    const [ref3City, setRef3City] = useState("");
    const [ref3State, setRef3State] = useState("");
    const [ref3Zip, setRef3Zip] = useState("");
    const [ref3Phone, setRef3Phone] = useState("");
    const [ref3Fax, setRef3Fax] = useState("");
    const [ref3Email, setRef3Email] = useState("");
    const [signature, setSignature] = useState("");
    const [title, setTitle] = useState("");
    const [bZip, setBZip] = useState("");
    const [bankLocation, setBankLocation] = useState("");
    const [yearEstablished, setYearEstablished] = useState("");
    const [fax, setFax] = useState("");
    const [bState, setBState] = useState("");
    const [dunsNum, setDunsNum] = useState("");
    const [subsidiary, setSubsidiary] = useState("");
    const [division, setDivision] = useState("");
    const [shippingAddress, setShippingAddress] = useState("");
    const [sCity, setSCity] = useState("");
    const [styleOfBusiness, setStyleOfBusiness] = useState("");
    const [sState, setSState] = useState("");
    const [sZip, setSZip] = useState("");
    const [businessCity, setBusinessCity] = useState("");
    const [businessState, setBusinessState] = useState("");
    const [businessZip, setBusinessZip] = useState("");
    const [tradeName, setTradeName] = useState("");
    const [phone, setPhone] = useState("");
    const [parentCompanyName, setParentCompanyName] = useState("");
    const [website, setWebsite] = useState("");
    const [accEmail, setAccEmail] = useState("");
    const [accFax, setAccFax] = useState("");
    const [accPhone, setAccPhone] = useState("");
    const [purchaseEmail, setPurchaseEmail] = useState("");
    const [purchaseFax, setPurchaseFax] = useState("");
    const [purchasePhone, setPurchasePhone] = useState("");
    const [federalTaxId, setFederalTaxId] = useState("");
    const [accNum, setAccNum] = useState("");
    const [willYourPurchasesBeTaxable, setWillYourPurchasesBeTaxable] = useState("");
    const [stateProvinceOfIncorporation, setStateProvinceOfIncorporation] = useState("");
    const [numOfEmployees, setNumOfEmployees] = useState("");
    const [contactName, setContactName] = useState("");
    const [purchaseContactName, setPurchaseContactName] = useState("");
    const [businessAddress, setBusinessAddress] = useState("");
    const [legalCustomerNameError, setLegalCustomerNameError] = useState(error);
    const [tradeNameError, setTradeNameError] = useState(error);
    const [willYourPurchasesBeTaxableError, setWillYourPurchasesBeTaxableError] = useState(error);
    const [billingAddressError, setBillingAddressError] = useState(error);
    const [salesRepresentativeError, setSalesRepresentativeError] = useState(error);
    const [dunsNumError, setDunsNumError] = useState(error);
    const [invoicesEmailError, setInvoicesEmailError] = useState(error);
    const [shippingAddressError, setShippingAddressError] = useState(error);
    const [bStateError, setBStateError] = useState(error);
    const [signatureError, setSignatureError] = useState(error);
    const [titleError, setTitleError] = useState(error);
    const [bankNameError, setBankNameError] = useState(error);
    const [bankContactNameError, setBankContactNameError] = useState(error);
    const [bankAddressError, setBankAddressError] = useState(error);
    const [bankCityError, setBankCityError] = useState(error);
    const [bankStateError, setBankStateError] = useState(error);
    const [bankZipError, setBankZipError] = useState(error);
    const [bankPhoneError, setBankPhoneError] = useState(error);
    const [bankFaxError, setBankFaxError] = useState(error);
    const [ref1NameError, setRef1NameError] = useState(error);
    const [ref1ContactNameError, setRef1ContactNameError] = useState(error);
    const [ref1AddressError, setRef1AddressError] = useState(error);
    const [ref1CityError, setRef1CityError] = useState(error);
    const [ref1StateError, setRef1StateError] = useState(error);
    const [ref1ZipError, setRef1ZipError] = useState(error);
    const [ref1PhoneError, setRef1PhoneError] = useState(error);
    const [ref1FaxError, setRef1FaxError] = useState(error);
    const [ref1EmailError, setRef1EmailError] = useState(error);
    const [ref2NameError, setRef2NameError] = useState(error);
    const [ref2ContactNameError, setRef2ContactNameError] = useState(error);
    const [ref2AddressError, setRef2AddressError] = useState(error);
    const [ref2CityError, setRef2CityError] = useState(error);
    const [ref2StateError, setRef2StateError] = useState(error);
    const [ref2ZipError, setRef2ZipError] = useState(error);
    const [ref2PhoneError, setRef2PhoneError] = useState(error);
    const [ref2FaxError, setRef2FaxError] = useState(error);
    const [ref2EmailError, setRef2EmailError] = useState(error);
    const [ref3NameError, setRef3NameError] = useState(error);
    const [ref3ContactNameError, setRef3ContactNameError] = useState(error);
    const [ref3AddressError, setRef3AddressError] = useState(error);
    const [ref3CityError, setRef3CityError] = useState(error);
    const [ref3StateError, setRef3StateError] = useState(error);
    const [ref3ZipError, setRef3ZipError] = useState(error);
    const [ref3PhoneError, setRef3PhoneError] = useState(error);
    const [ref3FaxError, setRef3FaxError] = useState(error);
    const [ref3EmailError, setRef3EmailError] = useState(error);
    const [sStateError, setSStateError] = useState(error);
    const [indicateAnticipatedMonthlyPurchasesError, setIndicateAnticipatedMonthlyPurchasesError] = useState(error);
    const [bankLocationError, setBankLocationError] = useState(error);
    const [subsidiaryError, setSubsidiaryError] = useState(error);
    const [federalTaxIdError, setFederalTaxIdError] = useState(error);
    const [numOfEmployeesError, setNumOfEmployeesError] = useState(error);
    const [bZipError, setBZipError] = useState(error);
    const [parentCompanyNameError, setParentCompanyNameError] = useState(error);
    const [faxError, setFaxError] = useState(error);
    const [stateProvinceOfIncorporationError, setStateProvinceOfIncorporationError] = useState(error);
    const [organizationTypeError, setOrganizationTypeError] = useState(error);
    const [phoneError, setPhoneError] = useState(error);
    const [websiteError, setWebsiteError] = useState(error);
    const [styleOfBusinessError, setStyleOfBusinessError] = useState(error);
    const [divisionError, setDivisionError] = useState(error);
    const [businessAddressError, setBusinessAddressError] = useState(error);
    const [bCityError, setBCityError] = useState(error);
    const [contactNameError, setContactNameError] = useState(error);
    const [sZipError, setSZipError] = useState(error);
    const [sCityError, setSCityError] = useState(error);
    const [businessZipError, setBusinessZipError] = useState(error);
    const [businessCityError, setBusinessCityError] = useState(error);
    const [businessStateError, setBusinessStateError] = useState(error);
    const [yearEstablishedError, setYearEstablishedError] = useState(error);
    const [accPhoneError, setAccPhoneError] = useState(error);
    const [accFaxError, setAccFaxError] = useState(error);
    const [accEmailError, setAccEmailError] = useState(error);
    const [purchasePhoneError, setPurchasePhoneError] = useState(error);
    const [purchaseFaxError, setPurchaseFaxError] = useState(error);
    const [purchaseEmailError, setPurchaseEmailError] = useState(error);
    const [purchaseContactNameError, setPurchaseContactNameError] = useState(error);
    const [accNumError, setAccNumError] = useState(error);

    const validateRequiredFields = (fieldName: string, fieldValue: any) => {
        if (!fieldValue || (typeof fieldValue === "string" && !fieldValue.trim())) {
            let errorMessage;

            switch (fieldName) {
                case "legalCustomerName":
                    errorMessage = "Legal Customer Name Required";
                    setLegalCustomerNameError(errorMessage);

                    break;
                case "salesRepresentative":
                    errorMessage = "Sales Representative Required";
                    setSalesRepresentativeError(errorMessage);

                    break;
                case "tradeName":
                    errorMessage = "Trade Name Required";
                    setTradeNameError(errorMessage);

                    break;
                case "signature":
                    errorMessage = "Submitted By Required";
                    setSignatureError(errorMessage);

                    break;
                case "title":
                    errorMessage = "Title Required";
                    setTitleError(errorMessage);

                    break;
                case "invoicesEmail":
                    errorMessage = "Invoices Email Required";
                    setInvoicesEmailError(errorMessage);

                    break;
                case "indicateAnticipatedMonthlyPurchases":
                    errorMessage = "Indicate Anticipated Monthly Purchases Required";
                    setIndicateAnticipatedMonthlyPurchasesError(errorMessage);

                    break;
                case "bankLocation":
                    errorMessage = "Location Required";
                    setBankLocationError(errorMessage);

                    break;

                case "bankName":
                    errorMessage = "Bank Name Required";
                    setBankNameError(errorMessage);

                    break;
                case "bankAddress":
                    errorMessage = "Bank Address Required";
                    setBankAddressError(errorMessage);

                    break;
                case "bankContactName":
                    errorMessage = "Bank Contact Name Required";
                    setBankContactNameError(errorMessage);

                    break;
                case "bankCity":
                    errorMessage = "City Required";
                    setBankCityError(errorMessage);

                    break;
                case "bankState":
                    errorMessage = "State Required";
                    setBankStateError(errorMessage);

                    break;
                case "bankZip":
                    errorMessage = "ZIP Required";
                    setBankZipError(errorMessage);

                    break;
                case "bankPhone":
                    errorMessage = "Phone Required";
                    setBankPhoneError(errorMessage);

                    break;
                case "bankFax":
                    errorMessage = "Fax Required";
                    setBankFaxError(errorMessage);

                    break;
                case "ref1Name":
                    errorMessage = "Name Required";
                    setRef1NameError(errorMessage);

                    break;
                case "ref1Address":
                    errorMessage = "Address Required";
                    setRef1AddressError(errorMessage);

                    break;
                case "ref1ContactName":
                    errorMessage = "Contact Name Required";
                    setRef1ContactNameError(errorMessage);

                    break;
                case "ref1City":
                    errorMessage = "City Required";
                    setRef1CityError(errorMessage);

                    break;
                case "ref1State":
                    errorMessage = "State Required";
                    setRef1StateError(errorMessage);

                    break;
                case "ref1Zip":
                    errorMessage = "ZIP Required";
                    setRef1ZipError(errorMessage);

                    break;
                case "ref1Phone":
                    errorMessage = "Phone Required";
                    setRef1PhoneError(errorMessage);

                    break;
                case "ref1Fax":
                    errorMessage = "Fax Required";
                    setRef1FaxError(errorMessage);

                    break;
                case "ref1Email":
                    errorMessage = "Email Address Required";
                    setRef1EmailError(errorMessage);

                    break;
                case "ref2Name":
                    errorMessage = "Name Required";
                    setRef2NameError(errorMessage);

                    break;
                case "ref2Address":
                    errorMessage = "Address Required";
                    setRef2AddressError(errorMessage);

                    break;
                case "ref2ContactName":
                    errorMessage = "Contact Name Required";
                    setRef2ContactNameError(errorMessage);

                    break;
                case "ref2City":
                    errorMessage = "City Required";
                    setRef2CityError(errorMessage);

                    break;
                case "ref2State":
                    errorMessage = "State Required";
                    setRef2StateError(errorMessage);

                    break;
                case "ref2Zip":
                    errorMessage = "ZIP Required";
                    setRef2ZipError(errorMessage);

                    break;
                case "ref2Phone":
                    errorMessage = "Phone Required";
                    setRef2PhoneError(errorMessage);

                    break;
                case "ref2Fax":
                    errorMessage = "Fax Required";
                    setRef2FaxError(errorMessage);

                    break;
                case "ref2Email":
                    errorMessage = "Email Address Required";
                    setRef2EmailError(errorMessage);

                    break;

                case "ref3Name":
                    errorMessage = "Name Required";
                    setRef3NameError(errorMessage);

                    break;
                case "ref3Address":
                    errorMessage = "Address Required";
                    setRef3AddressError(errorMessage);

                    break;
                case "ref3ContactName":
                    errorMessage = "Contact Name Required";
                    setRef3ContactNameError(errorMessage);

                    break;
                case "ref3City":
                    errorMessage = "City Required";
                    setRef3CityError(errorMessage);

                    break;
                case "ref3State":
                    errorMessage = "State Required";
                    setRef3StateError(errorMessage);

                    break;
                case "ref3Zip":
                    errorMessage = "ZIP Required";
                    setRef3ZipError(errorMessage);

                    break;
                case "ref3Phone":
                    errorMessage = "Phone Required";
                    setRef3PhoneError(errorMessage);

                    break;
                case "ref3Fax":
                    errorMessage = "Fax Required";
                    setRef3FaxError(errorMessage);

                    break;
                case "ref3Email":
                    errorMessage = "Email Address Required";
                    setRef3EmailError(errorMessage);

                    break;

                case "federalTaxId":
                    errorMessage = "Federal Tax Id Required";
                    setFederalTaxIdError(errorMessage);

                    break;
                case "billingAddress":
                    errorMessage = "Billing Address Required";
                    setBillingAddressError(errorMessage);

                    break;
                case "subsidiary":
                    errorMessage = "Subsidiary Required";
                    setSubsidiaryError(errorMessage);

                    break;
                case "shippingAddress":
                    errorMessage = "Shipping Address Required";
                    setShippingAddressError(errorMessage);

                    break;
                case "willYourPurchasesBeTaxable":
                    errorMessage = "Will Your Purchases Be Taxable Required";
                    setWillYourPurchasesBeTaxableError(errorMessage);

                    break;
                case "accPhone":
                    errorMessage = "Phone Required";
                    setAccPhoneError(errorMessage);

                    break;
                case "accFax":
                    errorMessage = "Fax Required";
                    setAccFaxError(errorMessage);

                    break;
                case "accEmail":
                    errorMessage = "Email Required";
                    setAccEmailError(errorMessage);

                    break;
                case "purchasePhone":
                    errorMessage = "Purchasing Phone Required";
                    setPurchasePhoneError(errorMessage);

                    break;
                case "purchaseFax":
                    errorMessage = "Purchasing Fax Required";
                    setPurchaseFaxError(errorMessage);

                    break;
                case "purchaseEmail":
                    errorMessage = "Purchasing Email Required";
                    setPurchaseEmailError(errorMessage);

                    break;
                case "accNum":
                    errorMessage = "Account Number Required";
                    setAccNumError(errorMessage);

                    break;
                case "dunsNum":
                    errorMessage = "Duns Number Required";
                    setDunsNumError(errorMessage);

                    break;
                case "contactName":
                    errorMessage = "Contact Name Required";
                    setContactNameError(errorMessage);

                    break;
                case "purchaseContactName":
                    errorMessage = "Contact Name Required";
                    setPurchaseContactNameError(errorMessage);

                    break;
                case "styleOfBusiness":
                    errorMessage = "Style of Business Required";
                    setStyleOfBusinessError(errorMessage);

                    break;
                case "division":
                    errorMessage = "Division Required";
                    setDivisionError(errorMessage);

                    break;
                case "bState":
                    errorMessage = "State Required";
                    setBStateError(errorMessage);

                    break;
                case "sState":
                    errorMessage = "State Required";
                    setSStateError(errorMessage);

                    break;
                case "website":
                    errorMessage = "Website Required";
                    setWebsiteError(errorMessage);

                    break;
                case "yearEstablished":
                    errorMessage = "Year Established Required";
                    setYearEstablishedError(errorMessage);

                    break;
                case "numOfEmployees":
                    errorMessage = "Number Of Employees Required";
                    setNumOfEmployeesError(errorMessage);

                    break;
                case "fax":
                    errorMessage = "Fax Required";
                    setFaxError(errorMessage);

                    break;
                case "organizationType":
                    errorMessage = "Organization Type Required";
                    setOrganizationTypeError(errorMessage);

                    break;
                case "stateProvinceOfIncorporation":
                    errorMessage = "State/Province of Incorporation Required";
                    setStateProvinceOfIncorporationError(errorMessage);

                    break;
                case "bZip":
                    errorMessage = "ZIP Required";
                    setBZipError(errorMessage);

                    break;
                case "sZip":
                    errorMessage = "ZIP Required";
                    setSZipError(errorMessage);

                    break;
                case "businessZip":
                    errorMessage = "ZIP Required";
                    setBusinessZipError(errorMessage);

                    break;
                case "phone":
                    errorMessage = "Phone Required";
                    setPhoneError(errorMessage);

                    break;
                case "businessAddress":
                    errorMessage = "Address Required";
                    setBusinessAddressError(errorMessage);

                    break;
                case "bCity":
                    errorMessage = "City Required";
                    setBCityError(errorMessage);

                    break;
                case "sCity":
                    errorMessage = "City Required";
                    setSCityError(errorMessage);

                    break;
                case "businessCity":
                    errorMessage = "City Required";
                    setBusinessCityError(errorMessage);

                    break;
                case "businessState":
                    errorMessage = "State Required";
                    setBusinessStateError(errorMessage);

                    break;
            }

            error = true;
        }
    };

    const validateAccEmailAddress = (email: string) => {
        if (email){
            const errorMessage = validateEmail(email);

            setAccEmailError(errorMessage);

            if (errorMessage) {
                error = true;
            }
        }
    };

    const validatePurchaseEmailAddress = (email: string) => {
        if (email){
            const errorMessage = validateEmail(email);

            setPurchaseEmailError(errorMessage);

            if (errorMessage) {
                error = true;
            }
        }
    };

    const validateInvoicesEmailAddress = (email: string) => {
        if (email){
            const errorMessage = validateEmail(email);

            setInvoicesEmailError(errorMessage);

            if (errorMessage) {
                error = true;
            }
        }
    };

    const validateRef1EmailAddress = (email: string) => {
        if (email){
            const errorMessage = validateEmail(email);

            setRef1EmailError(errorMessage);

            if (errorMessage) {
                error = true;
            }
        }
    };

    const validateRef2EmailAddress = (email: string) => {
        if (email){
            const errorMessage = validateEmail(email);

            setRef2EmailError(errorMessage);

            if (errorMessage) {
                error = true;
            }
        }
    };

    const validateRef3EmailAddress = (email: string) => {
        if (email){
            const errorMessage = validateEmail(email);

            setRef3EmailError(errorMessage);

            if (errorMessage) {
                error = true;
            }
        }
    };

    const handleChange = (fieldName: string, fieldValue: any) => {
        switch (fieldName) {
            case "legalCustomerName":
                setLegalCustomerNameError("");
                setLegalCustomerName(fieldValue);

                break;
            case "tradeName":
                setTradeNameError("");
                setTradeName(fieldValue);

                break;
            case "signature":
                setSignatureError("");
                setSignature(fieldValue);

                break;
            case "title":
                setTitleError("");
                setTitle(fieldValue);

                break;
            case "willYourPurchasesBeTaxable":
                setWillYourPurchasesBeTaxableError("");
                setWillYourPurchasesBeTaxable(fieldValue);

                break;
            case "dunsNum":
                setDunsNumError("");
                setDunsNum(fieldValue);

                break;
            case "indicateAnticipatedMonthlyPurchases":
                setIndicateAnticipatedMonthlyPurchasesError("");
                setIndicateAnticipatedMonthlyPurchases(fieldValue);

                break;
            case "bankName":
                setBankNameError("");
                setBankName(fieldValue);

                break;
            case "bankContactName":
                setBankContactNameError("");
                setBankContactName(fieldValue);

                break;
            case "bankAddress":
                setBankAddressError("");
                setBankAddress(fieldValue);

                break;
            case "bankCity":
                setBankCityError("");
                setBankCity(fieldValue);

                break;
            case "salesRepresentative":
                setSalesRepresentativeError("");
                setSalesRepresentative(fieldValue);

                break;
            case "bankState":
                setBankStateError("");
                setBankState(fieldValue);

                break;
            case "bankZip":
                setBankZipError("");
                setBankZip(fieldValue);

                break;
            case "bankPhone":
                setBankPhoneError("");
                setBankPhone(fieldValue);

                break;
            case "bankFax":
                setBankFaxError("");
                setBankFax(fieldValue);

                break;
            case "invoicesEmail":
                setInvoicesEmailError("");
                setInvoicesEmail(fieldValue);

                break;
            case "ref1Name":
                setRef1NameError("");
                setRef1Name(fieldValue);

                break;
            case "ref1ContactName":
                setRef1ContactNameError("");
                setRef1ContactName(fieldValue);

                break;
            case "ref1Address":
                setRef1AddressError("");
                setRef1Address(fieldValue);

                break;
            case "ref1City":
                setRef1CityError("");
                setRef1City(fieldValue);

                break;
            case "ref1State":
                setRef1StateError("");
                setRef1State(fieldValue);

                break;
            case "ref1Zip":
                setRef1ZipError("");
                setRef1Zip(fieldValue);

                break;
            case "ref1Phone":
                setRef1PhoneError("");
                setRef1Phone(fieldValue);

                break;
            case "ref1Fax":
                setRef1FaxError("");
                setRef1Fax(fieldValue);

                break;
            case "ref1Email":
                setRef1EmailError("");
                setRef1Email(fieldValue);

                break;
            case "ref2Name":
                setRef2NameError("");
                setRef2Name(fieldValue);

                break;
            case "ref2ContactName":
                setRef2ContactNameError("");
                setRef2ContactName(fieldValue);

                break;
            case "ref2Address":
                setRef2AddressError("");
                setRef2Address(fieldValue);

                break;
            case "ref2City":
                setRef2CityError("");
                setRef2City(fieldValue);

                break;
            case "ref2State":
                setRef2StateError("");
                setRef2State(fieldValue);

                break;
            case "ref2Zip":
                setRef2ZipError("");
                setRef2Zip(fieldValue);

                break;
            case "ref2Phone":
                setRef2PhoneError("");
                setRef2Phone(fieldValue);

                break;
            case "ref2Fax":
                setRef2FaxError("");
                setRef2Fax(fieldValue);

                break;
            case "ref2Email":
                setRef2EmailError("");
                setRef2Email(fieldValue);

                break;
            case "ref3Name":
                setRef3NameError("");
                setRef3Name(fieldValue);

                break;
            case "ref3ContactName":
                setRef3ContactNameError("");
                setRef3ContactName(fieldValue);

                break;
            case "ref3Address":
                setRef3AddressError("");
                setRef3Address(fieldValue);

                break;
            case "ref3City":
                setRef3CityError("");
                setRef3City(fieldValue);

                break;
            case "ref3State":
                setRef3StateError("");
                setRef3State(fieldValue);

                break;
            case "ref3Zip":
                setRef3ZipError("");
                setRef3Zip(fieldValue);

                break;
            case "ref3Phone":
                setRef3PhoneError("");
                setRef3Phone(fieldValue);

                break;
            case "ref3Fax":
                setRef3FaxError("");
                setRef3Fax(fieldValue);

                break;
            case "ref3Email":
                setRef3EmailError("");
                setRef3Email(fieldValue);

                break;

            case "bankLocation":
                setBankLocationError("");
                setBankLocation(fieldValue);

                break;
            case "federalTaxId":
                setFederalTaxIdError("");
                setFederalTaxId(fieldValue);

                break;
            case "bState":
                setBStateError("");
                setBState(fieldValue);

                break;
            case "subsidiary":
                setSubsidiaryError("");
                setSubsidiary(fieldValue);

                break;
            case "contactName":
                setContactNameError("");
                setContactName(fieldValue);

                break;
            case "purchaseContactName":
                setPurchaseContactNameError("");
                setPurchaseContactName(fieldValue);

                break;
            case "accPhone":
                setAccPhoneError("");
                setAccPhone(fieldValue);

                break;
            case "accFax":
                setAccFaxError("");
                setAccFax(fieldValue);

                break;
            case "accEmail":
                setAccEmailError("");
                setAccEmail(fieldValue);

                break;
            case "purchasePhone":
                setPurchasePhoneError("");
                setPurchasePhone(fieldValue);

                break;
            case "purchaseFax":
                setPurchaseFaxError("");
                setPurchaseFax(fieldValue);

                break;
            case "purchaseEmail":
                setPurchaseEmailError("");
                setPurchaseEmail(fieldValue);

                break;
            case "accNum":
                setAccNumError("");
                setAccNum(fieldValue);

                break;
            case "division":
                setDivisionError("");
                setDivision(fieldValue);

                break;
            case "businessState":
                setBusinessStateError("");
                setBusinessState(fieldValue);

                break;
            case "styleOfBusiness":
                setStyleOfBusinessError("");
                setStyleOfBusiness(fieldValue);

                break;
            case "stateProvinceOfIncorporation":
                setStateProvinceOfIncorporationError("");
                setStateProvinceOfIncorporation(fieldValue);

                break;
            case "parentCompanyName":
                setParentCompanyNameError("");
                setParentCompanyName(fieldValue);

                break;
            case "sState":
                setSStateError("");
                setSState(fieldValue);

                break;
            case "website":
                setWebsiteError("");
                setWebsite(fieldValue);

                break;
            case "numOfEmployees":
                setNumOfEmployeesError("");
                setNumOfEmployees(fieldValue);

                break;
            case "yearEstablished":
                setYearEstablishedError("");
                setYearEstablished(fieldValue);

                break;
            case "organizationType":
                setOrganizationTypeError("");
                setOrganizationType(fieldValue);

                break;
            case "bZip":
                setBZipError("");
                setBZip(fieldValue);

                break;
            case "businessZip":
                setBusinessZipError("");
                setBusinessZip(fieldValue);

                break;
            case "fax":
                setFaxError("");
                setFax(fieldValue);

                break;
            case "sZip":
                setSZipError("");
                setSZip(fieldValue);

                break;
            case "billingAddress":
                setBillingAddressError("");
                setBillingAddress(fieldValue);

                break;
            case "businessAddress":
                setBusinessAddressError("");
                setBusinessAddress(fieldValue);

                break;
            case "shippingAddress":
                setShippingAddressError("");
                setShippingAddress(fieldValue);

                break;
            case "phone":
                setPhoneError("");
                setPhone(fieldValue);

                break;
            case "address":
                setBusinessAddressError("");
                setBusinessAddress(fieldValue);

                break;
            case "bCity":
                setBCityError("");
                setBCity(fieldValue);

                break;
            case "sCity":
                setSCityError("");
                setSCity(fieldValue);

                break;
            case "businessCity":
                setBusinessCityError("");
                setBusinessCity(fieldValue);
                break;
        }
    };

    const today = new Date();
    // today.setDate(new Date().getDate());
    

    const [requestedDate, setRequestedDate] = useState(convertDateToDateOnlyString(new Date()));

    const handleSubmit = (e: any) => {
        e.preventDefault();
        continueHandlingFormSubmit();
    };

    const toasterContext: any = useContext(ToasterContext);

    const validatePhone = (name: string, value: any) => {
        switch (name) {
            case "phone":
                if (value && value.length < 11) {
                    setPhoneError("Phone Should Be Valid");
                    error = true;
                }
                break;

            case "fax":
                if (value && value.length < 11) {
                    setFaxError("Fax Should Be Valid");
                    error = true;
                }

                break;

            case "accPhone":
                if (value && value.length < 11) {
                    setAccPhoneError("Phone Should Be Valid");
                    error = true;
                }

                break;
            case "accFax":
                if (value && value.length < 11) {
                    setAccFaxError("Fax Should Be Valid");
                    error = true;
                }

                break;

            case "purchasePhone":
                if (value && value.length < 11) {
                    setPurchasePhoneError("Phone Should Be Valid");
                    error = true;
                }

                break;

            case "purchaseFax":
                if (value && value.length < 11) {
                    setPurchaseFaxError("Fax Should Be Valid");
                    error = true;
                }

                break;

            case "bankPhone":
                if (value && value.length < 11) {
                    setBankPhoneError("Phone Should Be Valid");
                    error = true;
                }

                break;

            case "bankFax":
                if (value && value.length < 11) {
                    setBankFaxError("Fax Should Be Valid");
                    error = true;
                }

                break;

            case "ref1Phone":
                if (value && value.length < 11) {
                    setRef1PhoneError("Phone Should Be Valid");
                    error = true;
                }

                break;

            case "ref2Phone":
                if (value && value.length < 11) {
                    setRef2PhoneError("Phone Should Be Valid");
                    error = true;
                }

                break;

            case "ref3Phone":
                if (value && value.length < 11) {
                    setRef3PhoneError("Phone Should Be Valid");
                    error = true;
                }

                break;

            case "ref1Fax":
                if (value && value.length < 11) {
                    setPhoneError("Fax Should Be Valid");
                    error = true;
                }

                break;

            case "ref2Fax":
                if (value && value.length < 11) {
                    setRef2FaxError("Fax Should Be Valid");
                    error = true;
                }

                break;

            case "ref3Fax":
                if (value && value.length < 11) {
                    setRef3FaxError("Fax Should Be Valid");
                    error = true;
                }

                break;
        }
    };

    const validateMonthlyPurchase = (monthlyPurchase: any) => {
        if (monthlyPurchase && Number(monthlyPurchase) <= 0) {
            setIndicateAnticipatedMonthlyPurchasesError("Monthly Purchase Should Be Valid");

            error = true;
        }
    };

    const validateYear = (year: string) => {
        if (year && year.length !== 4) {
            setYearEstablishedError("Year Should Be 4 Digits");

            error = true;
        }
    };

    const continueHandlingFormSubmit = () => {
        validateRequiredFields("legalCustomerName", legalCustomerName);
        validateRequiredFields("phone", phone);
        validateRequiredFields("salesRepresentative", salesRepresentative);
        validateRequiredFields("indicateAnticipatedMonthlyPurchases", indicateAnticipatedMonthlyPurchases);
        validateRequiredFields("federalTaxId", federalTaxId);
        validateRequiredFields("invoicesEmail", invoicesEmail);
        validateRequiredFields("billingAddress", billingAddress);
        validateRequiredFields("shippingAddress", shippingAddress);
        validateRequiredFields("businessAddress", businessAddress);
        validateRequiredFields("accPhone", accPhone);
        validateRequiredFields("accEmail", accEmail);
        validateRequiredFields("purchasePhone", purchasePhone);
        validateRequiredFields("purchaseEmail", purchaseEmail);
        validateRequiredFields("dunsNum", dunsNum);
        validateRequiredFields("sState", sState);
        validateRequiredFields("accNum", accNum);
        validateRequiredFields("bankCity", bankCity);
        validateRequiredFields("signature", signature);
        validateRequiredFields("title", title);
        validateRequiredFields("bankName", bankName);
        validateRequiredFields("bankContactName", bankContactName);
        validateRequiredFields("bankPhone", bankPhone);
        validateRequiredFields("bCity", bCity);
        validateRequiredFields("bState", bState);
        validateRequiredFields("bZip", bZip);
        validateRequiredFields("ref1Name", ref1Name);
        validateRequiredFields("ref1ContactName", ref1ContactName);
        validateRequiredFields("ref1Address", ref1Address);
        validateRequiredFields("ref1City", ref1City);
        validateRequiredFields("ref1State", ref1State);
        validateRequiredFields("ref1Zip", ref1Zip);
        validateRequiredFields("ref1Phone", ref1Phone);
        validateRequiredFields("ref1Email", ref1Email);
        validateRequiredFields("ref2Name", ref2Name);
        validateRequiredFields("ref2ContactName", ref2ContactName);
        validateRequiredFields("ref2Address", ref2Address);
        validateRequiredFields("ref2City", ref2City);
        validateRequiredFields("ref2State", ref2State);
        validateRequiredFields("ref2Zip", ref2Zip);
        validateRequiredFields("ref2Phone", ref2Phone);
        validateRequiredFields("ref2Email", ref2Email);
        validateRequiredFields("ref3Name", ref3Name);
        validateRequiredFields("ref3ContactName", ref3ContactName);
        validateRequiredFields("ref3Address", ref3Address);
        validateRequiredFields("ref3City", ref3City);
        validateRequiredFields("ref3State", ref3State);
        validateRequiredFields("ref3Zip", ref3Zip);
        validateRequiredFields("ref3Phone", ref3Phone);
        validateRequiredFields("ref3Email", ref3Email);
        validateRequiredFields("bankState", bankState);
        validateRequiredFields("bankZip", bankZip);
        validateRequiredFields("bankLocation", bankLocation);
        validateRequiredFields("bankAddress", bankAddress);
        validateRequiredFields("purchaseContactName", purchaseContactName);
        validateRequiredFields("contactName", contactName);
        validateRequiredFields("organizationType", organizationType);
        validateRequiredFields("sZip", sZip);
        validateRequiredFields("numOfEmployees", numOfEmployees);
        validateRequiredFields("stateProvinceOfIncorporation", stateProvinceOfIncorporation);
        validateRequiredFields("yearEstablished", yearEstablished);
        validateRequiredFields("website", website);
        validateRequiredFields("sCity", sCity);
        validateRequiredFields("businessCity", businessCity);
        validateRequiredFields("businessState", businessState);
        validateRequiredFields("businessZip", businessZip);
        validateRequiredFields("willYourPurchasesBeTaxable", willYourPurchasesBeTaxable);
        validateInvoicesEmailAddress(invoicesEmail);
        validatePurchaseEmailAddress(purchaseEmail);
        validateAccEmailAddress(accEmail);
        validateRef1EmailAddress(ref1Email);
        validateRef2EmailAddress(ref2Email);
        validateRef3EmailAddress(ref3Email);
        validatePhone("phone", phone);
        validatePhone("accPhone", accPhone);
        validatePhone("accFax", accFax);
        validatePhone("purchasePhone", purchasePhone);
        validatePhone("purchaseFax", purchaseFax);
        validatePhone("bankPhone", bankPhone);
        validatePhone("bankFax", bankFax);
        validatePhone("ref1Phone", ref1Phone);
        validatePhone("ref1Fax", ref1Fax);
        validatePhone("ref2Phone", ref2Phone);
        validatePhone("ref2Fax", ref2Fax);
        validatePhone("ref3Phone", ref3Phone);
        validatePhone("ref3Fax", ref3Fax);
        validatePhone("fax", fax);
        validateMonthlyPurchase(indicateAnticipatedMonthlyPurchases);
        validateYear(yearEstablished);

        if (error) {
            doScroll();
            return;
        }

        checkCriditApplicationForm({
            creditApplicationObj: {
                CompanyInformation: {
                    LegalCustomerName: legalCustomerName,
                    TradeDBAName: tradeName,
                    BillingAddress: billingAddress,
                    BCity: bCity,
                    BState: bState,
                    BZip: bZip,
                    ShippingAddress: shippingAddress,
                    SCity: sCity,
                    SState: sState,
                    SZip: sZip,
                    Phone: phone,
                    Fax: fax,
                    Website: website,
                },
                BusinessProfile: {
                    OrganizationType: organizationType,
                    YearEstablished: yearEstablished,
                    StateProvinceOfIncorporation: stateProvinceOfIncorporation,
                    NumberOfEmployees: numOfEmployees,
                    ParentCompanyName: parentCompanyName,
                    Address: businessAddress,
                    City: businessCity,
                    State: businessState,
                    ZIP: businessZip,
                    StyleOfBusiness: styleOfBusiness,
                    Subsidiary: subsidiary,
                    Division: division,
                    DunsNumber: dunsNum,
                },
                AccountsPayableInformation: {
                    ContactName: contactName,
                    Phone: accPhone,
                    Fax: accFax,
                    Email: accEmail,
                    EmailAddressForInvoices: invoicesEmail,
                },
                PurchasingInformation: {
                    ContactName: purchaseContactName,
                    Phone: purchasePhone,
                    Fax: purchaseFax,
                    Email: purchaseEmail,
                    EmailAddressForInvoices: invoicesEmail,
                },
                BankInformation: {
                    BankName: bankName,
                    BranchLocation: bankLocation,
                    AccountNumber: accNum,
                    City: bankCity,
                    State: bankState,
                    Zip: bankZip,
                    ContactName: bankContactName,
                    Phone: bankPhone,
                    Fax: bankFax,
                    Address: bankAddress,
                },
                TaxInformation: {
                    FederalTaxId: federalTaxId,
                    WillYourPurchasesBeTaxable: willYourPurchasesBeTaxable,
                },
                TradeCreditReferences: {
                    Ref1CompanyName: ref1Name,
                    Ref1Contact: ref1ContactName,
                    Ref1Address: ref1Address,
                    Ref1City: ref1City,
                    Ref1State: ref1State,
                    Ref1Zip: ref1Zip,
                    Ref1Phone: ref1Phone,
                    Ref1Fax: ref1Fax,
                    Ref1Email: ref1Email,
                    Ref2CompanyName: ref2Name,
                    Ref2Contact: ref2ContactName,
                    Ref2Address: ref2Address,
                    Ref2City: ref2City,
                    Ref2State: ref2State,
                    Ref2Zip: ref2Zip,
                    Ref2Phone: ref2Phone,
                    Ref2Fax: ref2Fax,
                    Ref2Email: ref2Email,
                    Ref3CompanyName: ref3Name,
                    Ref3Contact: ref3ContactName,
                    Ref3Address: ref3Address,
                    Ref3City: ref3City,
                    Ref3State: ref3State,
                    Ref3Zip: ref3Zip,
                    Ref3Phone: ref3Phone,
                    Ref3Fax: ref3Fax,
                    Ref3Email: ref3Email,
                    IndicateAnticipatedMonthlyPurchases: indicateAnticipatedMonthlyPurchases,
                    ProvideGcgSalesRepresentative: salesRepresentative,
                },
                BusinessCreditTerms: {
                    SubmittedBy: signature,
                    Title: title,
                    Date: requestedDate,
                },
            },
            onSuccess: () => {
                toasterContext.addToast({
                    body: "Credit Application is submitted",
                    messageType: "success",
                });
            },
            onError: () => {
                toasterContext.addToast({ body: "Error while submitting Credit Application", messageType: "danger" });
            },
        } as any);
    };

    const clearForm = (e: any) => {
        e.preventDefault();
        setLegalCustomerName("");
        setBillingAddress("");
        setOrganizationType("");
        setBCity("");
        setIndicateAnticipatedMonthlyPurchases("");
        setBankName("");
        setBankContactName("");
        setBankAddress("");
        setBankCity("");
        setBankState("");
        setBankZip("");
        setBankPhone("");
        setBankFax("");
        setInvoicesEmail("");
        setRef1Name("");
        setRef1ContactName("");
        setRef1Address("");
        setRef1City("");
        setRef1State("");
        setRef1Zip("");
        setRef1Phone("");
        setRef1Fax("");
        setRef1Email("");
        setRef2Name("");
        setRef2ContactName("");
        setRef2Address("");
        setRef2City("");
        setRef2State("");
        setRef2Zip("");
        setRef2Phone("");
        setRef2Fax("");
        setRef2Email("");
        setRef3Name("");
        setRef3ContactName("");
        setRef3Address("");
        setRef3City("");
        setRef3State("");
        setRef3Zip("");
        setRef3Phone("");
        setRef3Fax("");
        setRef3Email("");
        setSignature("");
        setTitle("");
        setBZip("");
        setBankLocation("");
        setYearEstablished("");
        setFax("");
        setBState("");
        setDunsNum("");
        setSubsidiary("");
        setDivision("");
        setShippingAddress("");
        setSCity("");
        setStyleOfBusiness("");
        setSState("");
        setSZip("");
        setBusinessCity("");
        setBusinessState("");
        setBusinessZip("");
        setTradeName("");
        setPhone("");
        setParentCompanyName("");
        setWebsite("");
        setAccEmail("");
        setAccFax("");
        setAccPhone("");
        setPurchaseEmail("");
        setPurchaseFax("");
        setPurchasePhone("");
        setFederalTaxId("");
        setAccNum("");
        setWillYourPurchasesBeTaxable("");
        setStateProvinceOfIncorporation("");
        setNumOfEmployees("");
        setContactName("");
        setPurchaseContactName("");
        setBusinessAddress("");
        setSalesRepresentative("");
        setRequestedDate(convertDateToDateOnlyString(today));
    };

    const handleSetRequestedDate = ({ selectedDay }: DatePickerState) => {
        const newDate = new Date(selectedDay!);
        setRequestedDate(convertDateToDateOnlyString(newDate));
    };

    return (
        <>
            <StyledWrapper {...styles.creditApplicationFormWrapper}>
                <form onSubmit={handleSubmit} id="custom-rfq-form" noValidate>
                    <GridContainer gap={10}>
                        <GridContainer>
                            <GridItem {...styles.titleGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Company Information")}</Typography>
                            </GridItem>
                        </GridContainer>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                label={translate("Legal Customer Name")}
                                onChange={e => handleChange("legalCustomerName", e.currentTarget.value)}
                                value={legalCustomerName}
                                data-test-selector="creditApplicationForm_firstName"
                                error={legalCustomerNameError}
                                maxLength={50}
                                placeholder={translate("Legal Customer Name")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                label={translate("Trade/DBA Name")}
                                onChange={e => handleChange("tradeName", e.currentTarget.value)}
                                value={tradeName}
                                data-test-selector="creditApplicationForm_tradeName"
                                maxLength={50}
                                placeholder={translate("Trade/DBA Name")}
                                {...styles.placeholderText}
                                {...{ optional: true }}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Phone")}
                                onChange={e => handleChange("phone", e.currentTarget.value)}
                                value={phone}
                                data-test-selector="creditApplicationForm_phone"
                                error={phoneError}
                                placeholder={translate("( ___ ) ___-____")}
                                required
                                min={9}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Fax")}
                                onChange={e => handleChange("fax", e.currentTarget.value)}
                                value={fax}
                                data-test-selector="creditApplicationForm_fax"
                                error={faxError}
                                placeholder={translate("( ___ ) ___-____")}
                                {...{ optional: true }}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                label={translate("Billing Address")}
                                onChange={e => handleChange("billingAddress", e.currentTarget.value)}
                                value={billingAddress}
                                data-test-selector="creditApplicationForm_firstName"
                                error={billingAddressError}
                                maxLength={50}
                                placeholder={translate("Billing Address")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="bCity"
                                label={translate("City")}
                                onChange={e => handleChange("bCity", e.currentTarget.value)}
                                value={bCity}
                                data-test-selector="creditApplicationForm_bcity"
                                error={bCityError}
                                maxLength={100}
                                placeholder={translate("City")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="bState"
                                label={translate("State")}
                                onChange={e => handleChange("bState", e.currentTarget.value)}
                                value={bState}
                                data-test-selector="creditApplicationForm_bState"
                                error={bStateError}
                                maxLength={100}
                                placeholder={translate("Billing State")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="bZip"
                                label={translate("ZIP")}
                                onChange={e => handleChange("bZip", e.currentTarget.value)}
                                value={bZip}
                                data-test-selector="creditApplicationForm_bZip"
                                error={bZipError}
                                maxLength={100}
                                placeholder={translate("ZIP")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                label={translate("Shipping Address")}
                                onChange={e => handleChange("shippingAddress", e.currentTarget.value)}
                                value={shippingAddress}
                                data-test-selector="creditApplicationForm_shippingAddress"
                                error={shippingAddressError}
                                maxLength={50}
                                placeholder={translate("Shipping Address")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="sCity"
                                label={translate("City")}
                                onChange={e => handleChange("sCity", e.currentTarget.value)}
                                value={sCity}
                                data-test-selector="creditApplicationForm_scity"
                                error={sCityError}
                                maxLength={100}
                                placeholder={translate("City")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="sState"
                                label={translate("State")}
                                onChange={e => handleChange("sState", e.currentTarget.value)}
                                value={sState}
                                data-test-selector="creditApplicationForm_bState"
                                error={sStateError}
                                maxLength={100}
                                placeholder={translate("Shipping State")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="sZip"
                                label={translate("ZIP")}
                                onChange={e => handleChange("sZip", e.currentTarget.value)}
                                value={sZip}
                                data-test-selector="creditApplicationForm_bZip"
                                error={sZipError}
                                maxLength={100}
                                placeholder={translate("ZIP")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>

                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="website"
                                label={translate("Website")}
                                onChange={e => handleChange("website", e.currentTarget.value)}
                                value={website}
                                data-test-selector="creditApplicationForm_website"
                                error={websiteError}
                                maxLength={100}
                                placeholder={translate("Website")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                    </GridContainer>
                    <GridContainer gap={10}>
                        <GridContainer>
                            <GridItem {...styles.titleGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Business Profile ")}</Typography>
                            </GridItem>
                        </GridContainer>
                        <GridItem {...styles.radioGridItem}>
                            <RadioGroup
                                {...styles.organizationRadioGroup}
                                id="organizationType"
                                label={translate("Organization Type")}
                                name="organizationType"
                                value={organizationType}
                                onChangeHandler={e => handleChange("organizationType", e.currentTarget.value)}
                                error={organizationTypeError}
                                required
                            >
                                <Radio value="Corp-Public">{translate("Corp-Public")}</Radio>
                                <Radio value="Corp-Private">{translate("Corp-Private")}</Radio>
                                <Radio value="LLC">{translate("LLC")}</Radio>
                                <Radio value="Govt Agency">{translate("Govt Agency")}</Radio>
                                <Radio value="Proprietorship/Partnership ">
                                    {translate("Proprietorship/Partnership ")}
                                </Radio>
                            </RadioGroup>
                        </GridItem>
                        <GridItem {...styles.numberOfEmployeesGridItem}>
                            <TextField
                                type="number"
                                name="yearEstablished"
                                label={translate("Year Established")}
                                onChange={e => handleChange("yearEstablished", e.currentTarget.value)}
                                value={yearEstablished}
                                data-test-selector="checkoutReviewAndSubmit_establishmentYear"
                                error={yearEstablishedError}
                                maxLength={4}
                                placeholder={translate("Year Established(YYYY)")}
                                {...styles.yearEstablishedTextField}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.yearEstablishedGridItem}>
                            <TextField
                                type="number"
                                label={translate("# of Employees")}
                                onChange={e => handleChange("numOfEmployees", e.currentTarget.value)}
                                value={numOfEmployees}
                                data-test-selector="creditApplicationForm_numOfEmployees"
                                error={numOfEmployeesError}
                                maxLength={50}
                                placeholder={translate("# of Employees")}
                                {...styles.numberOfEmployeesTextField}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                label={translate("State/Province of Incorporation")}
                                onChange={e => handleChange("stateProvinceOfIncorporation", e.currentTarget.value)}
                                value={stateProvinceOfIncorporation}
                                data-test-selector="creditApplicationForm_stateProvinceOfIncorporation"
                                error={stateProvinceOfIncorporationError}
                                maxLength={50}
                                placeholder={translate("State/Province of Incorporation")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="parentCompanyName"
                                label={translate("Parent Company Name")}
                                onChange={e => handleChange("parentCompanyName", e.currentTarget.value)}
                                value={parentCompanyName}
                                data-test-selector="creditApplicationForm_parentCompanyName"
                                error={parentCompanyNameError}
                                maxLength={100}
                                placeholder={translate("Parent Company Name (if applicable)")}
                                {...styles.placeholderText}
                                {...{ optional: true }}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="businessAddress"
                                label={translate("Address")}
                                onChange={e => handleChange("businessAddress", e.currentTarget.value)}
                                value={businessAddress}
                                data-test-selector="creditApplicationForm_businessAddress"
                                error={businessAddressError}
                                maxLength={100}
                                placeholder={translate("Address")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="businessCity"
                                label={translate("City")}
                                onChange={e => handleChange("businessCity", e.currentTarget.value)}
                                value={businessCity}
                                data-test-selector="creditApplicationForm_businessCity"
                                error={businessCityError}
                                maxLength={100}
                                placeholder={translate("City")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="businessState"
                                label={translate("State")}
                                onChange={e => handleChange("businessState", e.currentTarget.value)}
                                value={businessState}
                                data-test-selector="creditApplicationForm_businessState"
                                error={businessStateError}
                                maxLength={100}
                                placeholder={translate("State")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="businessZip"
                                label={translate("ZIP")}
                                onChange={e => handleChange("businessZip", e.currentTarget.value)}
                                value={businessZip}
                                data-test-selector="creditApplicationForm_businessZip"
                                error={businessZipError}
                                maxLength={100}
                                placeholder={translate("ZIP")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="styleOfBusiness"
                                label={translate("Style of Business")}
                                onChange={e => handleChange("styleOfBusiness", e.currentTarget.value)}
                                value={styleOfBusiness}
                                data-test-selector="creditApplicationForm_styleOfBusiness"
                                error={styleOfBusinessError}
                                maxLength={100}
                                placeholder={translate("Style of Business")}
                                {...styles.placeholderText}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="subsidiary"
                                label={translate("Subsidiary")}
                                onChange={e => handleChange("subsidiary", e.currentTarget.value)}
                                value={subsidiary}
                                data-test-selector="creditApplicationForm_subsidiary"
                                error={subsidiaryError}
                                maxLength={100}
                                placeholder={translate("Subsidiary")}
                                {...styles.placeholderText}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                name="division"
                                label={translate("Division")}
                                onChange={e => handleChange("division", e.currentTarget.value)}
                                value={division}
                                data-test-selector="creditApplicationForm_division"
                                error={divisionError}
                                maxLength={100}
                                placeholder={translate("Division")}
                                {...styles.placeholderText}
                            />
                        </GridItem>
                        <GridItem {...styles.cityGridItem}>
                            <TextField
                                type="number"
                                name="dunsNum"
                                label={translate("Duns #")}
                                onChange={e => handleChange("dunsNum", e.currentTarget.value)}
                                value={dunsNum}
                                data-test-selector="creditApplicationForm_DunsNum"
                                error={dunsNumError}
                                maxLength={100}
                                placeholder={translate("Duns #")}
                                {...styles.dunsNumberTextField}
                                required
                            />
                        </GridItem>
                    </GridContainer>
                    <GridContainer gap={10}>
                        <GridContainer gap={10} {...styles.accountPayPalContainer}>
                            {/* 111 */}
                            <GridContainer>
                                <GridItem {...styles.titleGridItem}>
                                    <Typography {...styles.sectionTitle}>
                                        {translate("Accounts Payable Information")}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    label={translate("Contact Name")}
                                    onChange={e => handleChange("contactName", e.currentTarget.value)}
                                    value={contactName}
                                    data-test-selector="creditApplicationForm_lastName"
                                    error={contactNameError}
                                    maxLength={50}
                                    placeholder={translate("Contact Name")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    type="tel"
                                    label={translate("Phone")}
                                    onChange={e => handleChange("accPhone", e.currentTarget.value)}
                                    value={accPhone}
                                    data-test-selector="creditApplicationForm_accPhone"
                                    error={accPhoneError}
                                    placeholder={translate("( ___ ) ___-____")}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    type="tel"
                                    label={translate("Fax")}
                                    onChange={e => handleChange("accFax", e.currentTarget.value)}
                                    value={accFax}
                                    data-test-selector="creditApplicationForm_accFax"
                                    error={accFaxError}
                                    placeholder={translate("( ___ ) ___-____")}
                                    {...{ optional: true }}
                                />
                            </GridItem>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    type="email"
                                    label={translate("Email Address")}
                                    onChange={e => handleChange("accEmail", e.currentTarget.value)}
                                    value={accEmail}
                                    data-test-selector="creditApplicationForm_accEmail"
                                    error={accEmailError}
                                    maxLength={256}
                                    minLength={5}
                                    placeholder={translate("Email@Address.com")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                        </GridContainer>
                        <GridContainer gap={10} {...styles.purchasingInformationContainer}>
                            {/* 222 */}

                            <GridContainer>
                                <GridItem {...styles.titleGridItem}>
                                    <Typography {...styles.sectionTitle}>
                                        {translate("Purchasing Information")}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    label={translate("Contact Name")}
                                    onChange={e => handleChange("purchaseContactName", e.currentTarget.value)}
                                    value={purchaseContactName}
                                    data-test-selector="creditApplicationForm_lastName"
                                    error={purchaseContactNameError}
                                    maxLength={50}
                                    placeholder={translate("Contact Name")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    type="tel"
                                    label={translate("Phone")}
                                    onChange={e => handleChange("purchasePhone", e.currentTarget.value)}
                                    value={purchasePhone}
                                    data-test-selector="creditApplicationForm_purchasePhone"
                                    error={purchasePhoneError}
                                    placeholder={translate("( ___ ) ___-____")}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    type="tel"
                                    label={translate("Fax")}
                                    onChange={e => handleChange("purchaseFax", e.currentTarget.value)}
                                    value={purchaseFax}
                                    data-test-selector="creditApplicationForm_purchaseFax"
                                    error={purchaseFaxError}
                                    placeholder={translate("( ___ ) ___-____")}
                                    {...{ optional: true }}
                                />
                            </GridItem>
                            <GridItem {...styles.formFullGridItem}>
                                <TextField
                                    type="email"
                                    label={translate("Email Address")}
                                    onChange={e => handleChange("purchaseEmail", e.currentTarget.value)}
                                    value={purchaseEmail}
                                    data-test-selector="creditApplicationForm_purchaseEmail"
                                    error={purchaseEmailError}
                                    maxLength={256}
                                    minLength={5}
                                    placeholder={translate("Email@Address.com")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem {...styles.InvoiceEmailGridItem}>
                                <TextField
                                    type="email"
                                    label={translate("Email address for Invoices")}
                                    onChange={e => handleChange("invoicesEmail", e.currentTarget.value)}
                                    value={invoicesEmail}
                                    data-test-selector="creditApplicationForm_invoicesEmail"
                                    error={invoicesEmailError}
                                    maxLength={256}
                                    minLength={5}
                                    placeholder={translate("Email@Address.com")}
                                    {...styles.placeholderText}
                                    required
                                />
                            </GridItem>
                        </GridContainer>
                    </GridContainer>
                    <GridContainer gap={10}>
                        <GridContainer>
                            <GridItem {...styles.titleGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Tax Information ")}</Typography>
                            </GridItem>
                        </GridContainer>
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <TextField
                                label={translate("Federal Tax ID #")}
                                onChange={e => handleChange("federalTaxId", e.currentTarget.value)}
                                value={federalTaxId}
                                data-test-selector="creditApplicationForm_FederalTaxId"
                                error={federalTaxIdError}
                                maxLength={50}
                                placeholder={translate("Federal Tax ID #")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.taxRadioGridItem}>
                            <RadioGroup
                                {...styles.willYourPurchasesBeTaxableRadioGroup}
                                id="willYourPurchasesBeTaxable"
                                label={translate("Will Your Purchases Be Taxable?")}
                                name="willYourPurchasesBeTaxable"
                                value={willYourPurchasesBeTaxable}
                                onChangeHandler={e => handleChange("willYourPurchasesBeTaxable", e.currentTarget.value)}
                                required
                                error={willYourPurchasesBeTaxableError}
                            >
                                <Radio value="Yes">{translate("Yes")}</Radio>
                                <Radio value="No">{translate("No")}</Radio>
                            </RadioGroup>
                        </GridItem>
                        <GridContainer>
                            <GridItem {...styles.TaxTextGridItem}>
                                <Typography>
                                    {siteMessage(
                                        "Custom_CreditApllication_TaxInformationText",
                                        "If no, please provide any and all Tax Exempt and/or Resale certifications.",
                                    )}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </GridContainer>
                    <GridContainer gap={10}>
                        <GridContainer>
                            <GridItem {...styles.titleGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Bank Information")}</Typography>
                            </GridItem>
                        </GridContainer>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Bank Name")}
                                onChange={e => handleChange("bankName", e.currentTarget.value)}
                                value={bankName}
                                data-test-selector="creditApplicationForm_bankName"
                                error={bankNameError}
                                maxLength={50}
                                placeholder={translate("Bank Name")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Branch Location")}
                                onChange={e => handleChange("bankLocation", e.currentTarget.value)}
                                value={bankLocation}
                                data-test-selector="creditApplicationForm_bankLocation"
                                error={bankLocationError}
                                maxLength={50}
                                placeholder={translate("Branch Location")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Account #")}
                                onChange={e => handleChange("accNum", e.currentTarget.value)}
                                value={accNum}
                                data-test-selector="creditApplicationForm_accNum"
                                error={accNumError}
                                maxLength={50}
                                placeholder={translate("Account #")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Address")}
                                onChange={e => handleChange("bankAddress", e.currentTarget.value)}
                                value={bankAddress}
                                data-test-selector="creditApplicationForm_bankAddress"
                                error={bankAddressError}
                                maxLength={50}
                                placeholder={translate("Bank Address")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="bankCity"
                                label={translate("City")}
                                onChange={e => handleChange("bankCity", e.currentTarget.value)}
                                value={bankCity}
                                data-test-selector="creditApplicationForm_bankCity"
                                error={bankCityError}
                                maxLength={100}
                                placeholder={translate("Bank City")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="bankState"
                                label={translate("State")}
                                onChange={e => handleChange("bankState", e.currentTarget.value)}
                                value={bankState}
                                data-test-selector="creditApplicationForm_bankState"
                                error={bankStateError}
                                maxLength={100}
                                placeholder={translate("Bank State")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="bankZip"
                                label={translate("ZIP")}
                                onChange={e => handleChange("bankZip", e.currentTarget.value)}
                                value={bankZip}
                                data-test-selector="creditApplicationForm_bankZip"
                                error={bankZipError}
                                maxLength={100}
                                placeholder={translate("ZIP")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="bankContactName"
                                label={translate("Bank Contact Name")}
                                onChange={e => handleChange("bankContactName", e.currentTarget.value)}
                                value={bankContactName}
                                data-test-selector="creditApplicationForm_website"
                                error={bankContactNameError}
                                maxLength={100}
                                placeholder={translate("Contact Name")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Phone")}
                                onChange={e => handleChange("bankPhone", e.currentTarget.value)}
                                value={bankPhone}
                                data-test-selector="creditApplicationForm_bankPhone"
                                error={bankPhoneError}
                                placeholder={translate("( ___ ) ___-____")}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Fax")}
                                onChange={e => handleChange("bankFax", e.currentTarget.value)}
                                value={bankFax}
                                data-test-selector="creditApplicationForm_bankFax"
                                error={bankFaxError}
                                placeholder={translate("( ___ ) ___-____")}
                                {...{ optional: true }}
                            />
                        </GridItem>
                    </GridContainer>

                    <GridContainer gap={10}>
                        <GridContainer>
                            <GridItem {...styles.titleGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Trade Credit References")}</Typography>
                            </GridItem>
                        </GridContainer>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Company Name")}
                                onChange={e => handleChange("ref1Name", e.currentTarget.value)}
                                value={ref1Name}
                                data-test-selector="creditApplicationForm_ref1Name"
                                error={ref1NameError}
                                maxLength={50}
                                placeholder={translate("Company Name")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Contact")}
                                onChange={e => handleChange("ref1ContactName", e.currentTarget.value)}
                                value={ref1ContactName}
                                data-test-selector="creditApplicationForm_ref1ContactName"
                                error={ref1ContactNameError}
                                maxLength={50}
                                placeholder={translate("Contact")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Address")}
                                onChange={e => handleChange("ref1Address", e.currentTarget.value)}
                                value={ref1Address}
                                data-test-selector="creditApplicationForm_ref1Address"
                                error={ref1AddressError}
                                maxLength={50}
                                placeholder={translate("Address")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref1City"
                                label={translate("City")}
                                onChange={e => handleChange("ref1City", e.currentTarget.value)}
                                value={ref1City}
                                data-test-selector="creditApplicationForm_Bank"
                                error={ref1CityError}
                                maxLength={100}
                                placeholder={translate("City")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref1State"
                                label={translate("State")}
                                onChange={e => handleChange("ref1State", e.currentTarget.value)}
                                value={ref1State}
                                data-test-selector="creditApplicationForm_ref1State"
                                error={ref1StateError}
                                maxLength={100}
                                placeholder={translate("State")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref1Zip"
                                label={translate("ZIP")}
                                onChange={e => handleChange("ref1Zip", e.currentTarget.value)}
                                value={ref1Zip}
                                data-test-selector="creditApplicationForm_ref1Zip"
                                error={ref1ZipError}
                                maxLength={100}
                                placeholder={translate("ZIP")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Phone")}
                                onChange={e => handleChange("ref1Phone", e.currentTarget.value)}
                                value={ref1Phone}
                                data-test-selector="creditApplicationForm_ref1Phone"
                                error={ref1PhoneError}
                                placeholder={translate("( ___ ) ___-____")}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Fax")}
                                onChange={e => handleChange("ref1Fax", e.currentTarget.value)}
                                value={ref1Fax}
                                data-test-selector="creditApplicationForm_ref1Fax"
                                error={ref1FaxError}
                                placeholder={translate("( ___ ) ___-____")}
                                {...{ optional: true }}
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="email"
                                label={translate("Email")}
                                onChange={e => handleChange("ref1Email", e.currentTarget.value)}
                                value={ref1Email}
                                data-test-selector="creditApplicationForm_ref1Email"
                                error={ref1EmailError}
                                maxLength={256}
                                minLength={5}
                                placeholder={translate("Email@Address.com")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                    </GridContainer>
                    <StyledWrapper {...styles.spacer} />
                    <GridContainer gap={10}>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Company Name")}
                                onChange={e => handleChange("ref2Name", e.currentTarget.value)}
                                value={ref2Name}
                                data-test-selector="creditApplicationForm_ref2Name"
                                error={ref2NameError}
                                maxLength={50}
                                placeholder={translate("Company Name")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Contact")}
                                onChange={e => handleChange("ref2ContactName", e.currentTarget.value)}
                                value={ref2ContactName}
                                data-test-selector="creditApplicationForm_ref2ContactName"
                                error={ref2ContactNameError}
                                maxLength={50}
                                placeholder={translate("Contact")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Address")}
                                onChange={e => handleChange("ref2Address", e.currentTarget.value)}
                                value={ref2Address}
                                data-test-selector="creditApplicationForm_ref2Address"
                                error={ref2AddressError}
                                maxLength={50}
                                placeholder={translate("Address")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref2City"
                                label={translate("City")}
                                onChange={e => handleChange("ref2City", e.currentTarget.value)}
                                value={ref2City}
                                data-test-selector="creditApplicationForm_ref2City"
                                error={ref2CityError}
                                maxLength={100}
                                placeholder={translate("City")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref2State"
                                label={translate("State")}
                                onChange={e => handleChange("ref2State", e.currentTarget.value)}
                                value={ref2State}
                                data-test-selector="creditApplicationForm_ref2State"
                                error={ref2StateError}
                                maxLength={100}
                                placeholder={translate("State")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref2Zip"
                                label={translate("ZIP")}
                                onChange={e => handleChange("ref2Zip", e.currentTarget.value)}
                                value={ref2Zip}
                                data-test-selector="creditApplicationForm_ref2Zip"
                                error={ref2ZipError}
                                maxLength={100}
                                placeholder={translate("ZIP")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Phone")}
                                onChange={e => handleChange("ref2Phone", e.currentTarget.value)}
                                value={ref2Phone}
                                data-test-selector="creditApplicationForm_ref2Phone"
                                error={ref2PhoneError}
                                placeholder={translate("( ___ ) ___-____")}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Fax")}
                                onChange={e => handleChange("ref2Fax", e.currentTarget.value)}
                                value={ref2Fax}
                                data-test-selector="creditApplicationForm_ref2Fax"
                                error={ref2FaxError}
                                placeholder={translate("( ___ ) ___-____")}
                                {...{ optional: true }}
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="email"
                                label={translate("Email")}
                                onChange={e => handleChange("ref2Email", e.currentTarget.value)}
                                value={ref2Email}
                                data-test-selector="creditApplicationForm_ref2Email"
                                error={ref2EmailError}
                                maxLength={256}
                                minLength={5}
                                placeholder={translate("Email@Address.com")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                    </GridContainer>
                    <StyledWrapper {...styles.spacer} />
                    <GridContainer gap={10}>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Company Name")}
                                onChange={e => handleChange("ref3Name", e.currentTarget.value)}
                                value={ref3Name}
                                data-test-selector="creditApplicationForm_ref3Name"
                                error={ref3NameError}
                                maxLength={50}
                                placeholder={translate("Company Name")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Contact")}
                                onChange={e => handleChange("ref3ContactName", e.currentTarget.value)}
                                value={ref3ContactName}
                                data-test-selector="creditApplicationForm_ref3ContactName"
                                error={ref3ContactNameError}
                                maxLength={50}
                                placeholder={translate("Contact")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Address")}
                                onChange={e => handleChange("ref3Address", e.currentTarget.value)}
                                value={ref3Address}
                                data-test-selector="creditApplicationForm_ref3Address"
                                error={ref3AddressError}
                                maxLength={50}
                                placeholder={translate("Address")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref3City"
                                label={translate("City")}
                                onChange={e => handleChange("ref3City", e.currentTarget.value)}
                                value={ref3City}
                                data-test-selector="creditApplicationForm_ref3City"
                                error={ref3CityError}
                                maxLength={100}
                                placeholder={translate("City")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref3State"
                                label={translate("State")}
                                onChange={e => handleChange("ref3State", e.currentTarget.value)}
                                value={ref3State}
                                data-test-selector="creditApplicationForm_ref3State"
                                error={ref3StateError}
                                maxLength={100}
                                placeholder={translate("State")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                name="ref3Zip"
                                label={translate("ZIP")}
                                onChange={e => handleChange("ref3Zip", e.currentTarget.value)}
                                value={ref3Zip}
                                data-test-selector="creditApplicationForm_ref3Zip"
                                error={ref3ZipError}
                                maxLength={100}
                                placeholder={translate("ZIP")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Phone")}
                                onChange={e => handleChange("ref3Phone", e.currentTarget.value)}
                                value={ref3Phone}
                                data-test-selector="creditApplicationForm_ref3Phone"
                                error={ref3PhoneError}
                                placeholder={translate("( ___ ) ___-____")}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="tel"
                                label={translate("Fax")}
                                onChange={e => handleChange("ref3Fax", e.currentTarget.value)}
                                value={ref3Fax}
                                data-test-selector="creditApplicationForm_ref3Fax"
                                error={ref3FaxError}
                                placeholder={translate("( ___ ) ___-____")}
                                {...{ optional: true }}
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                type="email"
                                label={translate("Email")}
                                onChange={e => handleChange("ref3Email", e.currentTarget.value)}
                                value={ref3Email}
                                data-test-selector="creditApplicationForm_ref3Email"
                                error={ref3EmailError}
                                maxLength={256}
                                minLength={5}
                                placeholder={translate("Email@Address.com")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <StyledWrapper {...styles.spacer} />
                        <GridItem {...styles.monthlyPurchasessectionTitleGridItem}>
                            <Typography {...styles.monthlyPurchasessectionTitle}>
                                {translate("Indicate anticipated monthly purchases from Genuine Cable Group, LLC")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.monthlyPurchasesInputGridItem}>
                            <Typography {...styles.dollerIcon}>{translate("$")}</Typography>
                            <TextField
                                type="number"
                                name="indicateAnticipatedMonthlyPurchases"
                                onChange={e =>
                                    handleChange("indicateAnticipatedMonthlyPurchases", e.currentTarget.value)
                                }
                                value={indicateAnticipatedMonthlyPurchases}
                                data-test-selector="creditApplicationForm_ref3Zip"
                                error={indicateAnticipatedMonthlyPurchasesError}
                                maxLength={100}
                                placeholder={translate("Monthly Purchase")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <TextField
                                label={translate("Provide GCG Sales Representative")}
                                onChange={e => handleChange("salesRepresentative", e.currentTarget.value)}
                                value={salesRepresentative}
                                data-test-selector="creditApplicationForm_salesRepresentative"
                                error={salesRepresentativeError}
                                maxLength={50}
                                placeholder={translate("Provide GCG Sales Representative")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                    </GridContainer>
                    <GridContainer gap={10}>
                        <GridContainer>
                            <GridItem {...styles.titleGridItem}>
                                <Typography {...styles.sectionTitle}>{translate("Business Credit Terms")}</Typography>
                            </GridItem>
                        </GridContainer>
                        <GridItem {...styles.zoneGridItem}>
                            <Zone contentId={id} zoneName="BusinessCreditTerms" />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Submitted By")}
                                onChange={e => handleChange("signature", e.currentTarget.value)}
                                value={signature}
                                data-test-selector="creditApplicationForm_signature"
                                error={signatureError}
                                maxLength={50}
                                placeholder={translate("Submitted By")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <TextField
                                label={translate("Title")}
                                onChange={e => handleChange("title", e.currentTarget.value)}
                                value={title}
                                data-test-selector="creditApplicationForm_title"
                                error={titleError}
                                maxLength={50}
                                placeholder={translate("Title")}
                                {...styles.placeholderText}
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.addressGridItem}>
                            <DatePicker
                                label={translate("Date")}
                                selectedDay={new Date(requestedDate)}
                                onDayChange={handleSetRequestedDate}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    value:today
                                }}
                                
                            />
                        </GridItem>
                    </GridContainer>
                    <StyledWrapper {...styles.spacer} />
                    <Button type="submit">{translate("Submit")}</Button>
                    <Button {...styles.clearButton} onClick={clearForm}>
                        {translate("Clear Form")}
                    </Button>
                </form>
            </StyledWrapper>
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CreditApplicationForm)),
    definition,
};

export default widgetModule;
